'use client';
import { useGetOrders } from '@api-product/🛍️-заказы/🛍️-заказы';
import { HistoryOrder } from '@api-product/models';
import { CardHistoryEntityMobile } from '@entities/mobile/cards';
import { ProfileHistoryWidget } from '@widgets/desktop/profile-history/ui/profile-history';
import { TablePurchasesRows } from '@widgets/desktop/profile-history/ui/profile-history.types';
import { usePathname, useRouter } from 'next/navigation';
import React, { RefObject, useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import { testData } from '@/mocks/profile-history-tables';
import { Divider } from '@/shared/ui';
import { InputAuth } from '@/shared/ui/desktop/input-auth';
import { Tabs, TabsItemFilter } from '@/shared/ui/tabs';

import styles from './profile-history.module.scss';

export const ProfileHistoryWidgetMobile = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pathname = usePathname();
  const slugs = pathname.split('/');
  const currentSlug = slugs[slugs.length - 1];

  return (
    <>
      {currentSlug === 'active-orders' && (
        <ProfileHistoryWidgetMobileActiveOrders />
      )}
      {currentSlug === 'withdraw' && <ProfileHistoryWidgetMobileWithdraw />}
      {currentSlug === 'purchases' && <ProfileHistoryWidgetMobilePurchases />}
    </>
  );
};

// eslint-disable-next-line react/display-name
export const ProfileHistoryWidgetMobileSearch = ({
  mRef,
}: {
  mRef?: RefObject<HTMLDivElement>;
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pathname = usePathname();
  const slugs = pathname.split('/');
  const currentSlug = slugs[slugs.length - 1];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const router = useRouter();

  const handleTabChange = async (
    tab: 'withdraw' | 'purchases' | 'active-orders',
  ) => {
    try {
      await router.push(`/profile/history/${tab}`);
    } catch (error) {
      console.error('Navigation failed:', error);
    }
  };

  return (
    <div ref={mRef} className={styles['search-block']}>
      <Tabs<'withdraw' | 'purchases' | 'active-orders'>
        className={styles['tabs-container']}
        defaultActiveTab={
          slugs[slugs.length - 1] as 'withdraw' | 'purchases' | 'active-orders'
        }
        onTabsChange={handleTabChange}
      >
        <TabsItemFilter value={'active-orders'}>Orders</TabsItemFilter>
        <TabsItemFilter value={'purchases'}>Purchases</TabsItemFilter>
        <TabsItemFilter value={'withdraw'}>Withdraw</TabsItemFilter>
      </Tabs>
      <InputAuth
        withErrorMessage={false}
        size={'s'}
        iconName={'magnifer'}
        value=""
        onChange={() => {}}
        className={styles['input-search']}
        placeholder="Date, amount, items..."
      />
    </div>
  );
};

// eslint-disable-next-line react/display-name
export const ProfileHistoryWidgetMobileActiveOrders = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [scrollParent, setScrollParentState] = useState<
    HTMLElement | undefined
  >(undefined);

  const activeOrdersStatuses: HistoryOrder['status'][] = [
    'PLACED',
    'PENDING',
    'PAID',
  ];

  const { data, isLoading } = useGetOrders();

  const rows =
    data?.data.rows.filter(item =>
      activeOrdersStatuses.includes(item.status),
    ) || [];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const scroll = document.getElementById('mobile-main');
    setScrollParentState(scroll ?? undefined);
  }, []);

  return Array.isArray(rows) && rows.length > 0 ? (
    <div className={styles['content']}>
      <Virtuoso<TablePurchasesRows>
        data={rows}
        customScrollParent={scrollParent}
        totalCount={100}
        style={{
          height: 'max-content',
          minHeight: '200dvh',
        }}
        itemContent={(idx, row) => (
          <>
            <CardHistoryEntityMobile.Orders
              orders={'active-orders'}
              row={row}
            />
            {idx !== rows.length - 1 && (
              <Divider style={{ minHeight: 1 }} direction={'horizontal'} />
            )}
          </>
        )}
      />
    </div>
  ) : (
    <ProfileHistoryWidget.Empty />
  );
};

// eslint-disable-next-line react/display-name
export const ProfileHistoryWidgetMobileWithdraw = () => {
  let rows = testData['withdraw'].rows;

  if (typeof window !== 'undefined' && window?.emptyHistoryScreen) {
    rows = testData['withdraw'].rows.slice(0, 0);
  }

  return Array.isArray(rows) && rows.length > 0 ? (
    <div className={styles['content']}>
      {rows.map((row, idx) => {
        return (
          <React.Fragment key={`withdraw-row-${row.id}-${idx}`}>
            <CardHistoryEntityMobile.Withdraw row={row} />
            {idx !== rows.length - 1 && (
              <Divider style={{ minHeight: 1 }} direction={'horizontal'} />
            )}
          </React.Fragment>
        );
      })}{' '}
    </div>
  ) : (
    <ProfileHistoryWidget.Empty />
  );
};

// eslint-disable-next-line react/display-name
export const ProfileHistoryWidgetMobilePurchases = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [scrollParent, setScrollParentState] = useState<
    HTMLElement | undefined
  >(undefined);

  const { data, isLoading } = useGetOrders();
  const purchasesStatuses: HistoryOrder['status'][] = ['COMPLETED', 'FAILED'];
  const rows =
    data?.data.rows.filter(item => purchasesStatuses.includes(item.status)) ||
    [];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const scroll = document.getElementById('mobile-main');
    setScrollParentState(scroll ?? undefined);
  }, []);

  return Array.isArray(rows) && rows.length > 0 ? (
    <div className={styles['content']}>
      <Virtuoso<TablePurchasesRows>
        data={rows}
        customScrollParent={scrollParent}
        totalCount={100}
        style={{
          height: 'max-content',
          minHeight: '200dvh',
        }}
        itemContent={(idx, row) => (
          <>
            <CardHistoryEntityMobile.Orders orders={'purchases'} row={row} />
            {idx !== rows.length - 1 && (
              <Divider style={{ minHeight: 1 }} direction={'horizontal'} />
            )}
          </>
        )}
      />
    </div>
  ) : (
    <ProfileHistoryWidget.Empty />
  );
};
